import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { htmlToPlainText } from '../utils/html';
import {
  STATUS,
  addDivider,
  markNotAttempted,
  moveDivider,
  questionLabel,
  questionStatuses,
  removeDivider,
  segmentsOf,
  setDividerQuestion,
  setTypedText,
  unmarkNotAttempted,
} from '../utils/questionSplit';

/**
 * Review for a task with more than one question.
 *
 * The student's whole paper is one stream of paragraphs; a divider says "the
 * answer to this question starts here". The proposer places the dividers it can
 * find, the teacher moves, adds or removes them, and any question left without a
 * divider is a decision — not attempted, typed in, or a divider added — before
 * marking can start. Nothing here is rendered for a single-question task.
 */

const FONT = "var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif)";
const PURPLE = '#7200e0';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  font-family: ${FONT};
`;

const Strip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const CHIP_COLOURS = {
  [STATUS.READY]: { bg: '#ede9fe', fg: '#5b00b5' },
  [STATUS.CHECK]: { bg: '#fef3c7', fg: '#92400e' },
  [STATUS.TYPED]: { bg: '#ede9fe', fg: '#5b00b5' },
  [STATUS.NOT_ATTEMPTED]: { bg: '#f3f4f6', fg: '#4b464f' },
  [STATUS.MISSING]: { bg: '#fee2e2', fg: '#b91c1c' },
};

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  background: ${({ $status }) => CHIP_COLOURS[$status]?.bg || '#f3f4f6'};
  color: ${({ $status }) => CHIP_COLOURS[$status]?.fg || '#4b464f'};
  white-space: nowrap;
`;

const Stream = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid rgba(201, 198, 204, 0.45);
  background: #fff;
  overflow-y: auto;
  min-height: 0;
  max-height: 60vh;
`;

const Paragraph = styled.p`
  margin: 0;
  padding: 2px 0 2px 30px;
  font-size: 14px;
  line-height: 22px;
  color: #1e252a;
  white-space: pre-wrap;
`;

const Gap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 30px;
  min-height: 22px;
`;

const GapSelect = styled.select.attrs({ className: 'inset-select-arrow' })`
  height: 24px;
  padding: 0 8px;
  border-radius: 999px;
  border: 1px dashed #c9c6cc;
  background: #fff;
  color: #6b7280;
  font-family: ${FONT};
  font-size: 12px;
  cursor: pointer;
  opacity: 0.55;
  &:hover,
  &:focus {
    opacity: 1;
    border-color: ${PURPLE};
    color: ${PURPLE};
  }
  &:disabled {
    display: none;
  }
`;

const DividerRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 6px 0;
`;

const DividerLine = styled.div`
  flex: 1;
  min-width: 40px;
  height: 0;
  border-top: 2px dashed
    ${({ $confidence }) => ($confidence === 'low' ? '#b45309' : PURPLE)};
`;

const DividerSelect = styled.select.attrs({ className: 'inset-select-arrow' })`
  height: 26px;
  padding: 0 8px;
  border-radius: 8px;
  border: 1px solid #c9c6cc;
  background: #fff;
  color: #1e252a;
  font-family: ${FONT};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  max-width: 320px;
`;

const Note = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: ${({ $warn }) => ($warn ? '#92400e' : '#6b7280')};
`;

const IconButton = styled.button`
  height: 26px;
  min-width: 26px;
  padding: 0 8px;
  border-radius: 999px;
  border: 1px solid #c9c6cc;
  background: #fff;
  color: #4b464f;
  font-family: ${FONT};
  font-size: 12px;
  cursor: pointer;
  &:hover:not(:disabled) {
    border-color: ${PURPLE};
    color: ${PURPLE};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Notice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 18px;
  background: ${({ $tone }) =>
    $tone === 'warn' ? '#fffbeb' : $tone === 'bad' ? '#fef2f2' : '#f9fafb'};
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'warn' ? '#fde68a' : $tone === 'bad' ? '#fecaca' : '#e5e7eb'};
  color: ${({ $tone }) =>
    $tone === 'warn' ? '#92400e' : $tone === 'bad' ? '#b91c1c' : '#4b464f'};
`;

const MissingPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1.5px dashed #c9c6cc;
  background: #fafafa;
`;

const MissingTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #1e252a;
`;

const MissingHint = styled.div`
  font-size: 13px;
  line-height: 18px;
  color: #6b7280;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ActionButton = styled.button`
  height: 32px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid ${PURPLE};
  background: ${({ $primary }) => ($primary ? PURPLE : '#fff')};
  color: ${({ $primary }) => ($primary ? '#fff' : PURPLE)};
  font-family: ${FONT};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${({ $primary }) =>
      $primary ? '#5b00b5' : 'rgba(241, 230, 252, 0.45)'};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TypedArea = styled.textarea`
  width: 100%;
  min-height: 96px;
  box-sizing: border-box;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #c9c6cc;
  font-family: ${FONT};
  font-size: 14px;
  line-height: 22px;
  color: #1e252a;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: ${PURPLE};
    box-shadow: 0 0 0 2px rgba(114, 0, 224, 0.12);
  }
`;

const STATUS_TEXT = {
  [STATUS.READY]: 'Ready',
  [STATUS.CHECK]: 'Check',
  [STATUS.TYPED]: 'Typed in',
  [STATUS.NOT_ATTEMPTED]: 'Not attempted',
  [STATUS.MISSING]: 'Not found',
};

const questionTitle = (question) => {
  const text = htmlToPlainText(question.question || '').trim();
  return text.length > 70 ? `${text.slice(0, 67)}…` : text;
};

const optionLabel = (question) =>
  `${questionLabel(question.serialNumber)} · ${
    questionTitle(question) || 'Untitled question'
  }`;

export const BulkUploadQuestionSplitReview = ({
  state,
  isLoading = false,
  loadError = '',
  onRetry,
  isReadOnly = false,
  onChange,
}) => {
  const [typingBySubmission, setTypingBySubmission] = useState({});
  const submissionKey = state?.submissionId ?? '';
  const typingFor = typingBySubmission[submissionKey] || {};

  const statuses = useMemo(
    () => (state ? questionStatuses(state) : []),
    [state]
  );
  const { leadingUnassigned } = useMemo(
    () => (state ? segmentsOf(state) : { segments: [], leadingUnassigned: [] }),
    [state]
  );
  const dividerAt = useMemo(() => {
    const map = new Map();
    (state?.dividers || []).forEach((divider) =>
      map.set(divider.paragraphIndex, divider)
    );
    return map;
  }, [state]);

  const update = (next) => {
    if (isReadOnly || next === state) return;
    onChange?.(next);
  };

  if (isLoading) {
    return (
      <Root data-component="bulk-upload-question-split" data-state="loading">
        <Notice>Reading where each question starts…</Notice>
      </Root>
    );
  }
  if (loadError) {
    return (
      <Root data-component="bulk-upload-question-split" data-state="error">
        <Notice $tone="bad">{loadError}</Notice>
        <Actions>
          <ActionButton type="button" onClick={onRetry}>
            Try again
          </ActionButton>
        </Actions>
      </Root>
    );
  }
  if (!state) return null;

  const missing = statuses.filter((row) => row.status === STATUS.MISSING);
  const decided = statuses.filter(
    (row) => row.status === STATUS.NOT_ATTEMPTED || row.status === STATUS.TYPED
  );
  const hasParagraphs = state.paragraphs.length > 0;
  const orderedQuestions = state.questions;

  return (
    <Root
      data-component="bulk-upload-question-split"
      data-state="ready"
      data-source={state.source || ''}
      data-missing={missing.length}
      data-dividers={state.dividers.length}
    >
      <Strip data-testid="question-split-status">
        {statuses.map((row) => (
          <Chip key={row.serialNumber} $status={row.status} title={row.note}>
            {questionLabel(row.serialNumber)} · {STATUS_TEXT[row.status]}
          </Chip>
        ))}
      </Strip>

      {!hasParagraphs && (
        <Notice $tone="warn">
          No text was read from this student's pages, so there is nothing to
          split. Type each answer in below, or mark the questions not attempted.
        </Notice>
      )}

      {hasParagraphs && (
        <Stream data-testid="question-split-stream">
          {leadingUnassigned.length > 0 && state.dividers.length > 0 && (
            <Notice $tone="warn">
              {leadingUnassigned.length === 1
                ? 'The first paragraph'
                : `The first ${leadingUnassigned.length} paragraphs`}{' '}
              sit before the first question and will not be marked. Move the
              first split up if they belong to it.
            </Notice>
          )}
          {state.paragraphs.map((paragraph) => {
            const divider = dividerAt.get(paragraph.index);
            return (
              <React.Fragment key={paragraph.index}>
                {divider ? (
                  <DividerRow
                    data-testid="question-split-divider"
                    data-serial={divider.serialNumber}
                  >
                    <DividerSelect
                      aria-label="Question this answer belongs to"
                      value={divider.serialNumber}
                      disabled={isReadOnly}
                      onChange={(event) =>
                        update(
                          setDividerQuestion(
                            state,
                            divider.id,
                            event.target.value
                          )
                        )
                      }
                    >
                      {orderedQuestions.map((question) => (
                        <option
                          key={question.serialNumber}
                          value={question.serialNumber}
                        >
                          {optionLabel(question)}
                        </option>
                      ))}
                    </DividerSelect>
                    <DividerLine $confidence={divider.confidence} />
                    <Note $warn={divider.confidence === 'low'}>
                      {divider.confidence === 'low' ? 'Not sure · ' : ''}
                      {divider.note}
                    </Note>
                    <IconButton
                      type="button"
                      aria-label="Move split up"
                      disabled={isReadOnly}
                      onClick={() => update(moveDivider(state, divider.id, -1))}
                    >
                      ↑
                    </IconButton>
                    <IconButton
                      type="button"
                      aria-label="Move split down"
                      disabled={isReadOnly}
                      onClick={() => update(moveDivider(state, divider.id, 1))}
                    >
                      ↓
                    </IconButton>
                    <IconButton
                      type="button"
                      aria-label="Remove split"
                      disabled={isReadOnly}
                      onClick={() => update(removeDivider(state, divider.id))}
                    >
                      Remove
                    </IconButton>
                  </DividerRow>
                ) : (
                  <Gap>
                    <GapSelect
                      aria-label={`Start a question at paragraph ${
                        paragraph.index + 1
                      }`}
                      value=""
                      disabled={isReadOnly}
                      onChange={(event) => {
                        if (event.target.value) {
                          update(
                            addDivider(
                              state,
                              paragraph.index,
                              event.target.value
                            )
                          );
                        }
                      }}
                    >
                      <option value="">+ Split here</option>
                      {orderedQuestions.map((question) => (
                        <option
                          key={question.serialNumber}
                          value={question.serialNumber}
                        >
                          {optionLabel(question)}
                        </option>
                      ))}
                    </GapSelect>
                  </Gap>
                )}
                <Paragraph data-testid="question-split-paragraph">
                  {htmlToPlainText(paragraph.html)}
                </Paragraph>
              </React.Fragment>
            );
          })}
        </Stream>
      )}

      {missing.map((row) => (
        <MissingPanel
          key={row.serialNumber}
          data-testid="question-split-missing"
          data-serial={row.serialNumber}
        >
          <MissingTitle>
            Nothing found for {questionLabel(row.serialNumber)}
            {questionTitle(row) ? ` · ${questionTitle(row)}` : ''}
          </MissingTitle>
          <MissingHint>
            {row.note ||
              'No answer to this question was found in the response.'}{' '}
            If the answer is in the text above, add a split where it starts. If
            it was left blank, mark it not attempted and it scores 0 for this
            question.
          </MissingHint>
          {typingFor[row.serialNumber] ? (
            <TypedArea
              aria-label={`Answer for ${questionLabel(row.serialNumber)}`}
              autoFocus
              disabled={isReadOnly}
              value={state.typed?.[row.serialNumber] || ''}
              onChange={(event) =>
                update(
                  setTypedText(state, row.serialNumber, event.target.value)
                )
              }
              placeholder="Type the student's answer"
            />
          ) : (
            <Actions>
              <ActionButton
                type="button"
                $primary
                disabled={isReadOnly}
                onClick={() =>
                  update(markNotAttempted(state, row.serialNumber))
                }
              >
                Mark as not attempted
              </ActionButton>
              <ActionButton
                type="button"
                disabled={isReadOnly}
                onClick={() =>
                  setTypingBySubmission((current) => ({
                    ...current,
                    [submissionKey]: {
                      ...current[submissionKey],
                      [row.serialNumber]: true,
                    },
                  }))
                }
              >
                Type it in
              </ActionButton>
            </Actions>
          )}
        </MissingPanel>
      ))}

      {decided.map((row) => (
        <MissingPanel
          key={row.serialNumber}
          data-testid="question-split-decided"
          data-serial={row.serialNumber}
        >
          <MissingTitle>
            {questionLabel(row.serialNumber)} ·{' '}
            {row.status === STATUS.TYPED
              ? 'typed in'
              : 'marked as not attempted'}
          </MissingTitle>
          {row.status === STATUS.TYPED ? (
            <TypedArea
              aria-label={`Answer for ${questionLabel(row.serialNumber)}`}
              disabled={isReadOnly}
              value={state.typed?.[row.serialNumber] || ''}
              onChange={(event) =>
                update(
                  setTypedText(state, row.serialNumber, event.target.value)
                )
              }
            />
          ) : (
            <Actions>
              <ActionButton
                type="button"
                disabled={isReadOnly}
                onClick={() =>
                  update(unmarkNotAttempted(state, row.serialNumber))
                }
              >
                Undo
              </ActionButton>
            </Actions>
          )}
        </MissingPanel>
      ))}
    </Root>
  );
};

export default BulkUploadQuestionSplitReview;
